import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import styles from './ColumnFilterHeader.module.scss';

export interface ColumnFilterOption<T extends string> {
  value: T;
  label: string;
  subtitle?: string;
}

interface ColumnFilterHeaderProps<T extends string> {
  label: string;
  options: ColumnFilterOption<T>[];
  value: T;
  defaultValue: T;
  onChange: (value: T) => void;
}

/**
 * A column header that doubles as its own filter control — label, a funnel icon that
 * opens a click-menu of options, and the currently-selected option shown as small text
 * right below the label so the active filter is visible without opening the menu. Used
 * consistently everywhere a column needs its own filter (Users tab's Role/Status, the
 * Customer Orgs listing's Plan/Status columns) instead of a separate dropdown outside
 * the table. Options may carry a subtitle for a two-line menu item, matching the plan
 * pickers elsewhere in this feature.
 *
 * The label always renders with `styles.headerCell`, matching the shared Table
 * component's own header typography exactly (see Table.module.scss's `.rowTh`) —
 * every column header across this feature should look identical, filterable or not.
 */
export function ColumnFilterHeader<T extends string>({
  label,
  options,
  value,
  defaultValue,
  onChange,
}: ColumnFilterHeaderProps<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isFiltered = value !== defaultValue;
  const selectedLabel = options.find((option) => option.value === value)?.label;

  return (
    <div className={styles.container}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <span className={styles.headerCell}>{label}</span>
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
          }}
        >
          <FilterListIcon fontSize="small" color={isFiltered ? 'primary' : 'disabled'} />
        </IconButton>
      </Stack>
      <span className={styles.headerFilterValue}>{selectedLabel}</span>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            className={styles.menuItem}
            onClick={() => {
              onChange(option.value);
              setAnchorEl(null);
            }}
          >
            {option.subtitle ? (
              <Stack>
                <Typography variant="body2" fontWeight={600}>
                  {option.label}
                </Typography>
                <Typography variant="body3" color="text.secondary">
                  {option.subtitle}
                </Typography>
              </Stack>
            ) : (
              option.label
            )}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
